/**
 * Streamer Redemption Shop Component
 *
 * Allows streamers to browse and redeem rewards using their points
 * All redemptions require admin approval before items are delivered
 */

Vue.component('streamer-redemption-shop', {
    template: templates.streamerRedemptionShop,
    data() {
        return {
            model: {
                catalog: [],
                myRequests: [],
                userPoints: {
                    total_points: 0,
                    lifetime_points: 0
                },
                selectedFilter: 'all', // 'all', 'frame', 'background', 'event_card', 'subscription'
                loading: false,
                error: null,
                abilities: {}
            }
        };
    },
    computed: {
        filteredCatalog() {
            const c = this;
            if (c.model.selectedFilter === 'all') {
                return c.model.catalog;
            }
            return c.model.catalog.filter(item => item.item_type === c.model.selectedFilter);
        },
        pendingRequests() {
            const c = this;
            return c.model.myRequests.filter(req => req.status === 'pending');
        },
        recentRequests() {
            const c = this;
            return c.model.myRequests.filter(req => req.status !== 'pending').slice(0, 5);
        }
    },
    mounted() {
        const c = this;
        c.getAbilities();
        c.loadData();
    },
    methods: {
        loadData() {
            const c = this;
            c.model.loading = true;
            c.model.error = null;

            // Load catalog, user requests, and user points
            Promise.all([
                c.loadCatalog(),
                c.loadMyRequests(),
                c.loadUserPoints()
            ])
                .then(() => {
                    c.model.loading = false;
                })
                .catch((error) => {
                    c.model.loading = false;
                    c.model.error = 'Failed to load data: ' + error.message;
                    console.error('Load data error:', error);
                });
        },

        loadCatalog() {
            const c = this;
            return redemptionService.getCatalog()
                .then((response) => {
                    c.model.catalog = response.data.catalog;
                })
                .catch((error) => {
                    console.error('Failed to load catalog:', error);
                    throw error;
                });
        },

        loadMyRequests() {
            const c = this;
            return redemptionService.getMyRequests()
                .then((response) => {
                    c.model.myRequests = response.data.requests;
                })
                .catch((error) => {
                    console.error('Failed to load requests:', error);
                    throw error;
                });
        },

        loadUserPoints() {
            const c = this;
            return axios.get('/api/streamer/rewards/balance.php')
                .then((response) => {
                    if (response.data.data) {
                        c.model.userPoints = {
                            total_points: response.data.data.total_points || 0,
                            lifetime_points: response.data.data.lifetime_points || 0
                        };
                    }
                })
                .catch((error) => {
                    console.error('Failed to load user points:', error);
                    // Set default values on error instead of throwing
                    c.model.userPoints = {
                        total_points: 0,
                        lifetime_points: 0
                    };
                });
        },

        getAbilities() {
            const c = this;
            return axios.get('/api/ability/get.php')
                .then((response) => {
                    if (response.data && response.data.data) {
                        const abilities = {};
                        for (let ability of response.data.data) {
                            abilities[ability.id] = ability;
                        }
                        c.model.abilities = abilities;
                    }
                })
                .catch((error) => {
                    console.error('Failed to load abilities:', error);
                    // Continue without abilities if loading fails
                });
        },

        setFilter(filter) {
            const c = this;
            c.model.selectedFilter = filter;
        },

        redeemItem(catalogItem) {
            const c = this;

            // Check if user has enough points first
            if (c.model.userPoints.total_points < catalogItem.point_cost) {
                alert(`Insufficient points. You have ${c.model.userPoints.total_points} points, but need ${catalogItem.point_cost} points to redeem this item.`);
                return;
            }

            // Validate user can redeem
            if (!c.canRedeem(catalogItem)) {
                return;
            }

            // Build confirmation message based on whether approval is required
            const requiresApproval = catalogItem.requires_approval !== 0;
            const approvalText = requiresApproval
                ? '\n\nThis item requires admin approval. Your request will be pending until approved.'
                : '\n\n✨ This item will be granted instantly!';
            const confirmMsg = `Redeem "${catalogItem.name}" for ${catalogItem.point_cost} points?${approvalText}`;

            if (!confirm(confirmMsg)) {
                return;
            }

            c.model.loading = true;

            redemptionService.createRedemptionRequest(catalogItem.id)
                .then((response) => {
                    if (response.data.success) {
                        // Different messages based on whether approval is required
                        const message = response.data.status === 'approved'
                            ? '✨ Item redeemed successfully! You can now use it.'
                            : 'Redemption request submitted successfully!\n\nYour request is pending admin approval. You will receive the item once approved.';
                        alert(message);
                        // Reload data to reflect new request
                        c.loadData();
                    } else {
                        alert('Failed to submit request: ' + response.data.message);
                        c.model.loading = false;
                    }
                })
                .catch((error) => {
                    console.error('Redemption error:', error);
                    alert('Failed to submit redemption request: ' + (error.response?.data?.message || error.message));
                    c.model.loading = false;
                });
        },

        // Silent check for whether item is redeemable (used in template :disabled binding)
        isRedeemable(catalogItem) {
            const c = this;

            // Check if user can redeem based on catalog rules
            if (catalogItem.can_redeem === false) {
                return false;
            }

            // Check if already owned
            if (catalogItem.already_owned) {
                return false;
            }

            // Check stock
            if (!catalogItem.is_in_stock) {
                return false;
            }

            // Check if user has pending request for this item
            const hasPendingRequest = c.model.myRequests.some(
                req => req.catalog_item_id === catalogItem.id && req.status === 'pending'
            );

            if (hasPendingRequest) {
                return false;
            }

            return true;
        },

        // Validation with alerts (called when user clicks redeem button)
        canRedeem(catalogItem) {
            const c = this;

            // Check if user can redeem based on catalog rules
            if (catalogItem.can_redeem === false) {
                alert('You have reached the maximum redemptions for this item.');
                return false;
            }

            // Check if already owned
            if (catalogItem.already_owned) {
                alert('You already own this item.');
                return false;
            }

            // Check stock
            if (!catalogItem.is_in_stock) {
                alert('This item is out of stock.');
                return false;
            }

            // Check if user has pending request for this item
            const hasPendingRequest = c.model.myRequests.some(
                req => req.catalog_item_id === catalogItem.id && req.status === 'pending'
            );

            if (hasPendingRequest) {
                alert('You already have a pending request for this item.');
                return false;
            }

            return true;
        },

        hasEnoughPoints(catalogItem) {
            const c = this;
            return c.model.userPoints.total_points >= catalogItem.point_cost;
        },

        getItemImageUrl(item) {
            const c = this;

            if (item.image_url) {
                return `${root}/${item.image_url}`;
            }

            if (item.item_image) {
                // Frame or background
                if (item.item_type === 'frame') {
                    return `${root}/images/frames/${item.item_id}/${item.item_image}`;
                } else if (item.item_type === 'background') {
                    return `${root}/images/backgrounds/${item.item_id}/${item.item_image}`;
                } else if (item.item_type === 'exclusive_card') {
                    return `${root}/images/exclusive-cards/${item.item_id}/${item.item_image}`;
                }
            }

            if (item.item_type === 'event_card') {
                return `${root}/images/cards/${item.item_id}.png`;
            }

            if (item.item_type === 'exclusive_card') {
                return `${root}/images/exclusive-cards/${item.item_id}.png`;
            }

            // Default placeholder based on type
            return c.getPlaceholderIcon(item.item_type);
        },

        getPlaceholderIcon(itemType) {
            const icons = {
                'frame': '🖼️',
                'background': '🌄',
                'event_card': '🃏',
                'exclusive_card': '🎴',
                'subscription': '⭐'
            };
            return icons[itemType] || '🎁';
        },

        getStatusBadgeClass(status) {
            const classes = {
                'pending': 'status-pending',
                'approved': 'status-approved',
                'rejected': 'status-rejected'
            };
            return classes[status] || 'status-unknown';
        },

        formatDate(dateString) {
            if (!dateString) return 'N/A';
            const date = new Date(dateString);
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
        },

        getItemTypeLabel(itemType) {
            const labels = {
                'frame': 'Frame',
                'background': 'Background',
                'event_card': 'Event Card',
                'exclusive_card': 'Exclusive Card',
                'subscription': 'Subscription'
            };
            return labels[itemType] || itemType;
        },

        getTierName(tierNumber) {
            const tiers = {
                '1': 'Standard',
                '2': 'Ultra',
                '3': 'Legendary',
                '4': 'Ultimate'
            };
            return tiers[tierNumber] || 'Unknown';
        },

        getExclusiveCardData(item) {
            const c = this;

            // Return null if not an exclusive card or event card
            if (!['exclusive_card', 'event_card'].includes(item.item_type)) {
                return null;
            }

            // Use card data from API response if available
            const cardData = item.card || item;

            // Create card object with base properties
            let card = {
                id: cardData.id || item.item_id,
                name: cardData.name || 'Unknown Card',
                type: cardData.type || 'Normal',
                tier: parseInt(cardData.tier) || 1,
                hp: parseInt(cardData.hp) || 0,
                attack: parseInt(cardData.attack) || 0,
                defence: parseInt(cardData.defence) || 0,
                accuracy: parseInt(cardData.accuracy) || 0,
                speed: parseInt(cardData.speed) || 0,
                ability: cardData.ability || 'No Ability',
                description: cardData.description || item.description || '',
                image: c.getItemImageUrl(item),
                selected: false,
                favorites: 0,
                quantity: null,
                cardNumber: null,
                count: null,
            };

            // Use playingCards.getCardStats to enhance card data with proper ability names and stat multipliers
            card = playingCards.getCardStats(card, c.model.abilities);

            return card;
        }
    }
});
